import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { API_BASE_URL } from '../config/environment';
import { useToast } from '../contexts/ToastContext';
import './Outfits.css';
import { proxyBucketUrl, getProductImageUrl, handleImageError } from '../utils/imageUtils';
import HotspotTooltipLayer from '../components/HotspotTooltipLayer';
import { useFavorites } from '../contexts/FavoritesContext';

interface OutfitData {
  id: number;
  title: string;
  description: string;
  image: string;
  images?: string[];
  hotspots: {
    id: number;
    x: number;
    y: number;
    productId: number;
    customTitle?: string;
    product?: {
      name: string;
      price: number;
      description?: string;
    };
  }[];
  isActive: boolean;
  order: number;
}

const Outfits: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { isFavorite: isFavoriteProduct, toggleFavorite: toggleFavoriteProduct } = useFavorites();
  const [hoveredSpots, setHoveredSpots] = useState<{[key: number]: number | null}>({});
  const [clickedSpots, setClickedSpots] = useState<{[key: number]: number | null}>({});

  // Add-to-cart / favourite buttons inside a hotspot tooltip
  const addHotspotToCart = async (productId: number) => {
    if (!api.isAuthenticated()) {
      showToast('Please login to add items to cart', 'error');
      return;
    }
    try {
      await api.addToCart({ productId, quantity: 1 });
      showToast('Added to cart!', 'success');
    } catch (error) {
      console.error('Failed to add to cart:', error);
      showToast('Failed to add to cart. Please try again.', 'error');
    }
  };

  const toggleHotspotFavorite = async (productId: number) => {
    try {
      await toggleFavoriteProduct(productId);
    } catch {
      showToast('Failed to update favorites. Please try again.', 'error');
    }
  };

  const toggleDesktopHotspot = (outfitId: number, hotspotId: number) => {
    setHoveredSpots(prev => ({...prev, [outfitId]: prev[outfitId] === hotspotId ? null : hotspotId}));
  };

  // Desktop (real mouse) opens tooltips on hover; touch keeps tap-to-open.
  // Closing waits a moment so the pointer can cross the gap into the tooltip.
  const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const hoverCloseTimer = useRef<number | undefined>(undefined);
  const openHotspotOnHover = (outfitId: number, hotspotId: number) => {
    if (!canHover) return;
    window.clearTimeout(hoverCloseTimer.current);
    setHoveredSpots({ [outfitId]: hotspotId });
  };
  const closeHotspotOnHover = () => {
    if (!canHover) return;
    hoverCloseTimer.current = window.setTimeout(() => setHoveredSpots({}), 200);
  };

  // Close any open desktop tooltip on a click outside it, instead of on hover-out.
  useEffect(() => {
    const hasOpenSpot = Object.values(hoveredSpots).some(v => v !== null && v !== undefined);
    if (!hasOpenSpot) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest('.hotspot, .hotspot-tooltip')) {
        setHoveredSpots({});
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [hoveredSpots]);

  const [outfitsData, setOutfitsData] = useState<OutfitData[]>([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  const [currentSlides, setCurrentSlides] = useState<{[outfitId: number]: number}>({});

  // Drag the looks strip with the mouse (touch already scrolls it natively).
  // Releasing after a long enough drag moves one look over, otherwise it
  // settles back on the nearest one.
  const mouseDrag = useRef<{ startX: number; scrollLeft: number; moved: boolean } | null>(null);

  const handleMouseDragStart = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType !== 'mouse' || e.button !== 0) return;
    if ((e.target as HTMLElement).closest('.hotspot, .hotspot-tooltip, button, a')) return;
    mouseDrag.current = { startX: e.clientX, scrollLeft: e.currentTarget.scrollLeft, moved: false };
    e.currentTarget.classList.add('is-dragging');
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handleMouseDragMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = mouseDrag.current;
    if (!drag) return;
    const dx = e.clientX - drag.startX;
    if (Math.abs(dx) > 5) drag.moved = true;
    e.currentTarget.scrollLeft = drag.scrollLeft - dx;
  };

  const handleMouseDragEnd = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = mouseDrag.current;
    if (!drag) return;
    mouseDrag.current = null;
    const container = e.currentTarget;
    container.classList.remove('is-dragging');
    if (!drag.moved) return;

    // A drag isn't a click: don't let it open a hotspot or follow a link
    const swallowClick = (ev: MouseEvent) => { ev.stopPropagation(); ev.preventDefault(); };
    container.addEventListener('click', swallowClick, { capture: true, once: true });
    window.setTimeout(() => container.removeEventListener('click', swallowClick, true), 0);

    const padding = parseFloat(getComputedStyle(container).scrollPaddingLeft) || 0;
    const starts = Array.from(container.querySelectorAll<HTMLElement>('.interactive-outfit.horizontal'))
      .map(card => card.offsetLeft - padding);
    if (starts.length === 0) return;
    const nearest = (x: number) => starts.reduce((best, s, i) => (Math.abs(s - x) < Math.abs(starts[best] - x) ? i : best), 0);

    const dx = e.clientX - drag.startX;
    const from = nearest(drag.scrollLeft);
    const target = Math.abs(dx) > 60
      ? Math.min(Math.max(from + (dx < 0 ? 1 : -1), 0), starts.length - 1)
      : nearest(container.scrollLeft);
    container.scrollTo({ left: starts[target], behavior: 'smooth' });
  };

  // Load outfits data from API and fetch product details
  useEffect(() => {
    const loadOutfitsData = async () => {
      try {
        console.log('🎨 Outfits: Starting to load outfits...');
        
        console.log('🌐 Using API URL:', API_BASE_URL);
        
        // Store API URL for debugging
        localStorage.setItem('outfits-api-url', API_BASE_URL);
        
        // Fetch outfits using the correct API URL with error handling
        let response;
        try {
          response = await fetch(`${API_BASE_URL}/admin/shop-the-look`, {
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            }
          });
          console.log('✅ API response status:', response.status);
        } catch (fetchError) {
          console.error('❌ Network error fetching outfits:', fetchError);
          localStorage.setItem('fetch-error', String(fetchError));
          throw new Error(`Network fetch failed: ${fetchError}`);
        }
        
        if (!response.ok) {
          throw new Error(`Failed to fetch outfits: ${response.status} ${response.statusText}`);
        }
        
        const lookData = await response.json();
        console.log('✅ Shop the Look loaded:', lookData);
        
        if (!lookData) {
          setOutfitsData([]);
          setLoading(false);
          return;
        }
        
        // Handle both single outfit object and array of outfits
        const outfitsArray = Array.isArray(lookData) ? lookData : [lookData];
        
        // Fetch product details for all hotspots
        const outfitsWithProductDetails = await Promise.all(
          outfitsArray.map(async (outfit: OutfitData) => {
            // Ensure hotspots is always an array
            const hotspots = Array.isArray(outfit.hotspots) ? outfit.hotspots : [];
            const hotspotsWithProducts = await Promise.all(
              hotspots.map(async (hotspot) => {
                if (hotspot.productId && hotspot.productId !== 0) {
                  try {
                    // Fetch product details directly with correct API URL
                    const response = await fetch(`${API_BASE_URL}/products/${hotspot.productId}`);
                    if (!response.ok) {
                      throw new Error(`Product API responded with ${response.status}`);
                    }
                    const product = await response.json();
                    console.log(`✅ Fetched product ${hotspot.productId}:`, product);
                    return {
                      ...hotspot,
                      product: {
                        name: product.name,
                        price: Number(product.price),
                        description: product.description,
                        images: product.images || []
                      }
                    };
                  } catch (error) {
                    console.error(`❌ Failed to fetch product ${hotspot.productId}:`, error);
                    // Return hotspot with placeholder data instead of undefined
                    return {
                      ...hotspot,
                      product: {
                        name: `Product ${hotspot.productId}`,
                        price: 0,
                        description: 'Product details unavailable'
                      }
                    };
                  }
                }
                // Return original hotspot if no product ID
                return hotspot;
              })
            );
            
            return {
              ...outfit,
              hotspots: hotspotsWithProducts
            };
          })
        );
        
        setOutfitsData(outfitsWithProductDetails);
        
        // Initialize hoveredSpots state for all outfits
        const initialHoveredSpots: {[key: number]: number | null} = {};
        outfitsWithProductDetails.forEach((outfit: OutfitData) => {
          initialHoveredSpots[outfit.id] = null;
        });
        setHoveredSpots(initialHoveredSpots);
      } catch (error) {
        console.error('Failed to load outfits data:', error);
        // Show empty state on error
        setOutfitsData([]);
        setHoveredSpots({});
        // Store error for debugging
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        localStorage.setItem('outfits-error', errorMessage);
      } finally {
        setLoading(false);
      }
    };

    loadOutfitsData();
  }, []);

  // Handle mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Click outside to close tooltips on mobile
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isMobile && !((event.target as Element)?.closest('.hotspot, .hotspot-tooltip'))) {
        setClickedSpots({});
      }
    };

    if (isMobile) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [isMobile]);



  const nextSlide = (outfitId: number, outfit: OutfitData) => {
    const images = outfit.images || [outfit.image];
    const currentSlide = currentSlides[outfitId] || 0;
    const nextSlideIndex = currentSlide < images.length - 1 ? currentSlide + 1 : 0;
    setCurrentSlides(prev => ({...prev, [outfitId]: nextSlideIndex}));
  };

  const prevSlide = (outfitId: number, outfit: OutfitData) => {
    const images = outfit.images || [outfit.image];
    const currentSlide = currentSlides[outfitId] || 0;
    const prevSlideIndex = currentSlide > 0 ? currentSlide - 1 : images.length - 1;
    setCurrentSlides(prev => ({...prev, [outfitId]: prevSlideIndex}));
  };


  if (loading) {
    return (
      <div className="outfits-page">
        <div className="outfits-page-header">
          <h1>Shop the Look</h1>
          <p>Loading curated vintage outfits...</p>
        </div>
      </div>
    );
  }

  if (outfitsData.length === 0) {
    return (
      <div className="outfits-page">
        <div className="outfits-page-header">
          <h1>Shop the Look</h1>
          <p>No outfits available at the moment.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="outfits-page">
      <div className="outfits-page-header">
        <h1>Shop the Look</h1>
        <p>Explore our styled outfits for inspiration & shop your favorite pieces along the way .𖥔 ݁⋆.˚</p>
      </div>

      <div className="scroll-section-wrapper">
        <div
          className="horizontal-scroll-container"
          onPointerDown={handleMouseDragStart}
          onPointerMove={handleMouseDragMove}
          onPointerUp={handleMouseDragEnd}
          onPointerCancel={handleMouseDragEnd}
        >
          <div className="outfits-horizontal-wrapper">
          {outfitsData.map((outfit) => (
            <section key={outfit.id} className="interactive-outfit horizontal">
              <div className="outfits-container">
                <div className="outfits-image-wrapper">
                  <div className="outfits-image">
                    {(() => {
                      const images = outfit.images || [outfit.image];
                      const currentSlide = currentSlides[outfit.id] || 0;
                      const currentImage = images[currentSlide];
                      
                      return (
                        <>
                          <img 
                            src={proxyBucketUrl(currentImage)} 
                            alt={outfit.title}
                            className="outfit-main-image"
                            draggable={false}
                          />
                          
                          {/* Show slide navigation arrows only if multiple images */}
                          {images.length > 1 && (
                            <>
                              <button 
                                className="slide-nav-arrow slide-nav-left"
                                onClick={() => prevSlide(outfit.id, outfit)}
                              >
&lt;
                              </button>
                              <button 
                                className="slide-nav-arrow slide-nav-right"
                                onClick={() => nextSlide(outfit.id, outfit)}
                              >
&gt;
                              </button>
                            </>
                          )}
                        </>
                      );
                    })()}
                    
                    {/* Show hotspots only on first slide */}
                    {(currentSlides[outfit.id] || 0) === 0 && outfit.hotspots && Array.isArray(outfit.hotspots) && outfit.hotspots.map((hotspot, index) => {
                      const isTooltipVisible = isMobile 
                        ? clickedSpots[outfit.id] === hotspot.id
                        : hoveredSpots[outfit.id] === hotspot.id;
                        
                      return (
                        <div
                          key={hotspot.id}
                          className={`hotspot clickable ${isTooltipVisible ? 'hotspot--open' : ''}`}
                          style={{ top: `${hotspot.y}%`, left: `${hotspot.x}%` }}
                          onMouseEnter={() => !isMobile && openHotspotOnHover(outfit.id, hotspot.id)}
                          onMouseLeave={() => !isMobile && closeHotspotOnHover()}
                          onClick={() => {
                            if (isMobile) {
                              // On mobile: tap opens the tooltip; tapping again (inside
                              // the tooltip) is what navigates to the product.
                              setClickedSpots(prev => ({...prev, [outfit.id]: hotspot.id}));
                            } else {
                              // On desktop: hover opens it and a click goes straight to the product;
                              // without a real mouse, click toggles it. Clicking outside (handled above) closes it.
                              if (canHover) {
                                if (hotspot.product && hotspot.productId !== -1) {
                                  navigate(`/product/${hotspot.productId}`);
                                }
                              } else {
                                toggleDesktopHotspot(outfit.id, hotspot.id);
                              }
                            }
                          }}
                        >
                          <div className="hotspot-dot">
                            <div className="hotspot-pulse"></div>
                            <div className="hotspot-ring"></div>
                          </div>
                          {isTooltipVisible && (hotspot.product || hotspot.productId === -1) && (
                            <HotspotTooltipLayer>
                              <div
                                className={`hotspot-tooltip hotspot-tooltip--glass ${hotspot.product && hotspot.productId !== -1 ? 'hotspot-tooltip--clickable' : ''}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (hotspot.product && hotspot.productId !== -1) {
                                    navigate(`/product/${hotspot.productId}`);
                                  }
                                }}
                              >
                                {hotspot.productId === -1 ? (
                                  <div style={{ textAlign: 'center', padding: '0.6rem 0.8rem' }}>
                                    <h4 style={{ color: '#1a1a1a', margin: '0 0 0.2rem 0', fontSize: '0.95rem' }}>{hotspot.customTitle || 'SOLD'}</h4>
                                    <div style={{ fontSize: '1.1rem', margin: '0.1rem 0', transform: 'rotate(90deg)', display: 'inline-block', color: '#1a1a1a' }}>:(</div>
                                    <p style={{ margin: '0.2rem 0 0', fontSize: '0.75rem', color: '#4b4b4b', lineHeight: '1.2' }}>Unfortunately, this product is no longer available.</p>
                                  </div>
                                ) : hotspot.product ? (
                                  <>
                                    <div className="tooltip-product">
                                      <img className="tooltip-thumb" src={getProductImageUrl(hotspot.product)} alt="" onError={handleImageError} />
                                      <div className="tooltip-body">
                                        <h4 className="tooltip-title">{hotspot.product.name}</h4>
                                        <div className="tooltip-footer">
                                          <span className="hotspot-price">€{hotspot.product.price.toFixed(2)}</span>
                                          <div className="tooltip-actions">
                                            <button
                                              className={`hotspot-icon-btn ${isFavoriteProduct(hotspot.productId) ? 'active' : ''}`}
                                              aria-label="Add to favorites"
                                              onClick={(e) => { e.stopPropagation(); toggleHotspotFavorite(hotspot.productId); }}
                                            >
                                              <svg width="18" height="18" viewBox="0 0 24 24" fill={isFavoriteProduct(hotspot.productId) ? 'currentColor' : 'none'} xmlns="http://www.w3.org/2000/svg">
                                                <path d="M12 2L15.09 8.26L22 9L17 13.74L18.18 20.74L12 17.77L5.82 20.74L7 13.74L2 9L8.91 8.26L12 2Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
                                              </svg>
                                            </button>
                                            <button
                                              className="hotspot-icon-btn"
                                              aria-label="Add to cart"
                                              onClick={(e) => { e.stopPropagation(); addHotspotToCart(hotspot.productId); }}
                                            >
                                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M6.2 9h11.6l0.9 11a1 1 0 0 1-1 1.1H6.3a1 1 0 0 1-1-1.1L6.2 9z" />
                                                <path d="M9.6 9V5.9a2.4 2.4 0 0 1 4.8 0V9" />
                                              </svg>
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </>
                                ) : null}
                                <div className="tooltip-arrow"></div>
                              </div>
                            </HotspotTooltipLayer>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="outfit-info">
                  <div className="info-header">
                    <h2>{outfit.title}</h2>
                    <div className="header-decoration">
                      <div className="deco-line"></div>
                      <div className="deco-dot"></div>
                      <div className="deco-line"></div>
                    </div>
                  </div>
                  
                  <p className="look-description">{outfit.description}</p>
                </div>
              </div>
            </section>
          ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Outfits;